/*
 * The workspace token contract, in one place.
 *
 * `src/styles/brand-tokens.css` is the sheet; this module is the list of names it
 * is required to declare, so the sheet and the code that verifies it can never
 * drift. CommonJS on purpose: the CRA bundle imports it (webpack interop makes
 * `import { WS_TOKENS } from '../styles/wsTokens'` work) and the Playwright token
 * harness `require`s the same file, and one list beats two that agree by hand.
 *
 * Naming: every workspace custom property is `--ws-` prefixed. `tokens.css`
 * already owns `--ink`, `--muted`, `--line`, `--accent`, `--ok`, `--warn`,
 * `--danger`, `--shadow-*` and `--font-mono` on `:root` with different values,
 * and `wt-` is the legacy work-trial namespace. No `--ws-*` token is ever
 * declared on bare `:root`.
 */

/** The six shipped house kits. A kit id outside this list renders as "custom". */
const WS_BRANDS = ['meridian', 'halyard', 'bellhouse', 'copperline', 'fernwell', 'northmark'];

/**
 * The neutral house kit. A trial whose snapshot names no kit, or whose kit cannot
 * be resolved, wears this one — the workspace is never Appli-branded, so there is
 * always a house to fall back to. Which kit is neutral is a property of the shipped
 * set, so it lives here beside the set and not in the code that falls back to it.
 */
const WS_NEUTRAL_BRAND = 'meridian';

/** The `data-brand` value for a kit that is not one of the six. Declared by no block. */
const WS_CUSTOM_BRAND = 'custom';

/** The two themes the ramp is declared for. `data-theme` is always written explicitly. */
const WS_THEMES = ['light', 'dark'];

/**
 * The kit's six token keys — the closed shape of `BrandKit.tokens` and of
 * `WorkTrial.brand_kit_snapshot.tokens` — mapped to the custom-property stem each
 * one lands on. Each key carries a {light, dark} pair, so each stem yields two
 * declarations: `--ws-<stem>-l` and `--ws-<stem>-d`. Six keys x two themes = the
 * twelve values a `[data-brand]` block declares, and nothing else.
 *
 * The stems are deliberately distinct from the ramp names they feed (brand -> accent,
 * cast -> canvas/pane), so an input can never be mistaken for a derived value.
 */
const WS_KIT_STEMS = {
    accent: 'brand',
    accent_contrast: 'on',
    surface: 'cast',
    surface_alt: 'cast2',
    ink: 'type',
    ink_muted: 'type2',
};

/** `['--ws-brand-l', '--ws-brand-d', ...]` — the twelve kit inputs, in key order. */
const WS_KIT_INPUTS = Object.values(WS_KIT_STEMS).flatMap((stem) => [`--ws-${stem}-l`, `--ws-${stem}-d`]);

/**
 * The derived ramp. Every name here is declared once in `.ws-root[data-theme="light"]`
 * and once in `.ws-root[data-theme="dark"]`, and nowhere else.
 */
const WS_COLOR_TOKENS = [
    '--ws-accent',
    '--ws-accent-hover',
    '--ws-on-accent',
    '--ws-accent-ink',
    '--ws-wash',
    '--ws-wash-strong',
    '--ws-canvas',
    '--ws-pane',
    '--ws-pane-2',
    '--ws-ink',
    '--ws-ink-2',
    '--ws-muted',
    '--ws-faint',
    '--ws-line',
    '--ws-line-2',
    '--ws-ok',
    '--ws-ok-wash',
    '--ws-warn',
    '--ws-warn-wash',
    '--ws-danger',
    '--ws-danger-wash',
    '--ws-away',
    '--ws-away-wash',
    '--ws-p-1',
    '--ws-p-2',
    '--ws-p-3',
    '--ws-p-4',
    '--ws-p-5',
    '--ws-p-6',
    '--ws-p-7',
    '--ws-p-8',
];

/** Ramp tokens that are not colours, so they carry no `color-mix` and no `@property`. */
const WS_NON_COLOR_TOKENS = [
    '--ws-shadow-1',
    '--ws-shadow-2',
    '--ws-r-sm',
    '--ws-r-md',
    '--ws-r-lg',
    '--ws-r-pill',
    '--ws-spring',
    '--ws-fast',
    '--ws-font-display',
    '--ws-font-ui',
    '--ws-font-mono',
];

/** Every derived token the sheet declares per theme. */
const WS_TOKENS = [...WS_COLOR_TOKENS, ...WS_NON_COLOR_TOKENS];

module.exports = {
    WS_BRANDS,
    WS_NEUTRAL_BRAND,
    WS_CUSTOM_BRAND,
    WS_THEMES,
    WS_KIT_STEMS,
    WS_KIT_INPUTS,
    WS_COLOR_TOKENS,
    WS_NON_COLOR_TOKENS,
    WS_TOKENS,
};
